import type {
  QmsBrandingMode,
  QmsDisplay,
  QmsDisplayBranding,
  QmsDisplayKind,
  QmsDisplayLayout,
  QmsLabelSize,
} from './types';

export interface BrandingFormState {
  mode: QmsBrandingMode;
  file_id: string;
}

export interface DisplayFormState {
  slug: string;
  name: string;
  kind: QmsDisplayKind;
  group_id: string;
  counter_id: string;
  title: string;
  title_ar: string;
  logo_url: string;
  logo_file_id: string;
  accent_color: string;
  label_color: string;
  label_size: QmsLabelSize;
  show_clock: boolean;
  branding: BrandingFormState;
}

export const EMPTY_BRANDING: BrandingFormState = { mode: 'none', file_id: '' };

export const EMPTY_DISPLAY_FORM: DisplayFormState = {
  slug: '',
  name: '',
  kind: 'group',
  group_id: '',
  counter_id: '',
  title: '',
  title_ar: '',
  logo_url: '',
  logo_file_id: '',
  accent_color: '',
  label_color: '',
  label_size: 'md',
  show_clock: true,
  branding: EMPTY_BRANDING,
};

/** Flatten a stored display's layout JSON into editable form state. */
export function hydrateDisplayForm(display: QmsDisplay): DisplayFormState {
  const layout: QmsDisplayLayout = display.layout ?? { kind: 'group' };
  const branding = layout.branding ?? {};
  return {
    slug: display.slug,
    name: display.name,
    kind: hydrateKind(layout.kind),
    group_id: layout.group_id ?? '',
    counter_id: layout.counter_id ?? '',
    title: layout.title ?? '',
    title_ar: layout.title_ar ?? '',
    logo_url: layout.logo_url ?? '',
    logo_file_id: layout.logo_file_id ?? '',
    accent_color: layout.accent_color ?? '',
    label_color: layout.label_color ?? '',
    label_size: layout.label_size ?? 'md',
    show_clock: layout.show_clock !== false,
    branding: {
      mode: branding.mode ?? 'none',
      file_id: branding.file_id ?? '',
    },
  };
}

/** Shape editable form state back into the layout JSON the API stores. */
export function buildDisplayLayout(form: DisplayFormState): QmsDisplayLayout {
  const common: Partial<QmsDisplayLayout> = {};
  const title = form.title.trim();
  const titleAr = form.title_ar.trim();
  const logoFileId = form.logo_file_id.trim();
  const logo = form.logo_url.trim();
  if (title) common.title = title;
  if (titleAr) common.title_ar = titleAr;
  // An uploaded logo wins; a legacy URL is kept only when none is set.
  if (logoFileId) common.logo_file_id = logoFileId;
  else if (logo) common.logo_url = logo;
  if (form.accent_color) common.accent_color = form.accent_color;
  if (form.label_color) common.label_color = form.label_color;
  if (form.label_size !== 'md') common.label_size = form.label_size;

  if (form.kind === 'counter') {
    const layout: QmsDisplayLayout = { kind: 'counter', ...common };
    if (form.counter_id) layout.counter_id = form.counter_id;
    return layout;
  }
  const layout: QmsDisplayLayout = {
    kind: form.kind,
    show_clock: form.show_clock,
    branding: pruneBranding(form.branding),
    ...common,
  };
  if (form.kind === 'group' && form.group_id) layout.group_id = form.group_id;
  return layout;
}

function hydrateKind(value: QmsDisplayLayout['kind'] | undefined): QmsDisplayKind {
  if (value === 'counter' || value === 'all') return value;
  return 'group';
}

function pruneBranding(branding: BrandingFormState): QmsDisplayBranding {
  if (branding.mode === 'none' || !branding.file_id) return { mode: 'none' };
  return { mode: branding.mode, file_id: branding.file_id };
}
